import React, { useState } from 'react';
import { Sheet, SheetContent, SheetOverlay } from '@frontend/shadcn/components/ui/sheet';
import { Button } from '@frontend/shadcn/components/ui/button';
import { cn } from '@frontend/shadcn/lib/utils';
import {
  FileIcon,
  XmarkIcon,
  CheckIcon,
  EnvelopeIcon,
  CircleDollarIcon
} from '@revfluence/fresh-icons/regular/esm';
import { NewspaperIcon } from '@revfluence/fresh-icons/solid/esm';
import { useStageTemplates } from '@frontend/app/hooks/useStageTemplates';
import type { GetStageTemplates_getStageTemplates as IStageTemplate, GetStageTemplates_getStageTemplates_statuses as ITemplateStatus } from '@frontend/app/queries/types/GetStageTemplates';

// Template visual configurations
const templateConfigs: Record<string, { icon: React.ReactNode; iconBgColor: string }> = {
  'Ad-Ready Content': {
    icon: <FileIcon className="text-white text-xl" />,
    iconBgColor: 'bg-pink-600'
  },
  'Affiliate Newsletter': {
    icon: <NewspaperIcon className="text-white text-xl" />,
    iconBgColor: 'bg-gray-500'
  },
  'Concept Review': {
    icon: <FileIcon className="text-white text-xl" />,
    iconBgColor: 'bg-indigo-500'
  },
  'Content Rates': {
    icon: <CircleDollarIcon className="text-white text-xl" />,
    iconBgColor: 'bg-amber-300'
  },
  'Welcome Email': {
    icon: <EnvelopeIcon className="text-white text-xl" />,
    iconBgColor: 'bg-blue-400'
  }
};

const StatusBadge: React.FC<{ status: ITemplateStatus }> = ({ status }) => {
  return (
    <div
      data-icon="False"
      data-state="Default"
      data-type="Regular"
      className={cn('px-2 py-0.5 rounded-lg flex justify-center items-center gap-1', status.bgColor)}
    >
      <div className="justify-start text-gray-800 text-xs font-normal font-['Inter'] leading-tight">{status.name}</div>
    </div>
  );
};

interface TemplateCardProps {
  template: IStageTemplate;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, isSelected, onSelect }) => {
  const config = templateConfigs[template.name] || {
    icon: <FileIcon className="text-white text-xl" />,
    iconBgColor: 'bg-gray-500'
  };

  return (
    <div
      className={cn(
        'self-stretch px-4 py-3 bg-white rounded-2xl outline outline-1 outline-offset-[-1px]',
        'inline-flex justify-start items-start gap-2 relative',
        isSelected ? 'outline-gray-800' : 'outline-gray-200'
      )}
      onClick={() => onSelect(template.id.toString())}
    >
      <div className={cn('w-12 h-12 p-2 rounded-xl flex justify-center items-center', config.iconBgColor)}>
        {config.icon}
      </div>
      <div className="flex-1 inline-flex flex-col justify-start items-start gap-2">
        <div className="self-stretch flex flex-col justify-start items-start">
          <div className="self-stretch inline-flex justify-start items-center">
            <div className="flex-1 justify-start text-gray-800 text-sm font-medium font-['Inter'] leading-normal">{template.name}</div>
          </div>
          <div className="self-stretch flex flex-col justify-center items-start gap-1">
            <div className="self-stretch justify-start text-gray-500 text-xs font-normal font-['Inter'] leading-tight">{template.description}</div>
          </div>
        </div>
        <div className="self-stretch inline-flex justify-start items-start gap-1 flex-wrap content-start">
          {template.statuses.map((status, index) => (
            <StatusBadge key={index} status={status} />
          ))}
        </div>
      </div>
      {isSelected && (
        <div className="h-4 absolute -top-1 -right-1 bg-white rounded-lg outline outline-2 outline-white flex justify-center items-center">
          <CheckIcon className="text-gray-800 text-base" />
        </div>
      )}
    </div>
  );
};

interface TemplatesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (template: IStageTemplate) => void;
}

export const TemplatesDrawer: React.FC<TemplatesDrawerProps> = ({
  open,
  onOpenChange,
  onSelect
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const { templates, loading, error } = useStageTemplates();

  const handleSelect = (id: string) => {
    setSelectedTemplate(id);
  };

  const handleLaunch = () => {
    if (selectedTemplate) {
      const template = templates.find((t) => t.id.toString() === selectedTemplate);
      if (template) {
        onSelect(template); // Pass the complete template object
        onOpenChange(false);
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetOverlay className="bg-black/20" />
      <SheetContent
        side="right"
        className="w-[40vw] min-w-[480px] max-w-[720px] p-0 flex flex-col [&>button:first-of-type]:hidden"
      >
        <div className="w-full px-6 py-5 bg-white border-b border-gray-200 flex justify-between items-center">
          <div className="text-gray-900 text-[20px] font-medium leading-[28px]">
            Custom Stage Templates
          </div>
          <div
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center cursor-pointer"
            onClick={() => onOpenChange(false)}
          >
            <XmarkIcon className="w-4 h-4 text-gray-800" />
          </div>
        </div>

        <div className="flex-1 w-full overflow-auto">
          <div className="self-stretch p-4 inline-flex flex-col justify-start items-start gap-4">
            <div className="self-stretch flex flex-col justify-start items-start gap-2">
              {loading ? (
                <div className="w-full text-center py-4">Loading templates...</div>
              ) : error ? (
                <div className="w-full text-center py-4 text-red-500">Error loading templates</div>
              ) : templates.length === 0 ? (
                <div className="w-full text-center py-4">No templates available</div>
              ) : (
                templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    isSelected={selectedTemplate === template.id.toString()}
                    onSelect={handleSelect}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        <div className="w-full px-6 py-5 bg-white border-t border-gray-200 flex gap-3">
          <div className="flex justify-start items-center gap-2">
            <Button
              variant="default"
              className="h-8 px-3 py-1 bg-gray-800 rounded-lg flex justify-start items-center gap-2 hover:bg-gray-900"
              disabled={!selectedTemplate}
              onClick={handleLaunch}
            >
              <div className="flex justify-center items-center gap-2.5">
                <div className="justify-start text-white text-sm font-medium font-['Inter'] leading-normal">
                  Launch
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-8 px-3 py-1 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 flex justify-start items-center gap-2"
              onClick={() => onOpenChange(false)}
            >
              <div className="flex justify-center items-center gap-2.5">
                <div className="justify-start text-gray-800 text-sm font-medium font-['Inter'] leading-normal">
                  Cancel
                </div>
              </div>
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
